import { Level2OuterPageLayout } from "@octopusdeploy/design-system-components";
import { Permission, ProcessType } from "@octopusdeploy/octopus-server-client";
import type { RedirectRouteDefinition, RouteTemplate, UnknownQueryParam } from "@octopusdeploy/portal-routes";
import { allRoutes, links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { Route, Routes, useLocation, useParams } from "react-router-dom";
import ConfigurationLayout from "~/areas/configuration/components/ConfigurationLayout/ConfigurationLayout";
import DeploymentToProjectTaskRedirect from "~/areas/deployments/DeploymentToProjectTaskRedirect";
import { ArgoCDInstanceLayout } from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDInstanceLayout";
import { MachineLayout, WorkerMachineLayout } from "~/areas/infrastructure/components/MachineLayout";
import { InsightsReportLoader } from "~/areas/insights/components/InsightsReportLayout/InsightsReportLoader";
import LicenseChecker from "~/areas/insights/components/InsightsRoutes/LicenseChecker";
import { RedirectFeedBasedOnType } from "~/areas/library/components/ExternalFeeds/RedirectFeedBasedOnType";
import StepTemplateLayout from "~/areas/library/components/StepTemplates/StepTemplateLayout";
import { ProjectTemplateLayout } from "~/areas/platformHub/projectTemplates/ProjectTemplateLayout";
import { ProjectLayout } from "~/areas/projects/components/ProjectLayout/ProjectLayout";
import BranchAwareRedirect from "~/areas/projects/components/ProjectsRoutes/BranchAwareRedirect";
import ProjectTenantVariablesRedirect from "~/areas/projects/components/ProjectsRoutes/ProjectTenantVariablesRedirect";
import { ProjectToOverviewRedirect } from "~/areas/projects/components/ProjectsRoutes/ProjectToOverviewRedirect";
import { VariablesBranchAwareRedirect } from "~/areas/projects/components/ProjectsRoutes/VariablesBranchAwareRedirect";
import RunbookContextLayout from "~/areas/projects/components/Runbooks/RunbookContextLayout";
import RunbookRunToRunbookTaskRedirect from "~/areas/runbookRuns/RunbookRunToRunbookTaskRedirect";
import { InterruptionToProjectRedirect } from "~/areas/tasks/components/TaskRoutes/InterruptionToTaskRedirect";
import TenantLayout from "~/areas/tenants/TenantLayout/TenantLayout";
import MyProfileLayout from "~/areas/users/MyProfileLayout";
import { session } from "~/clientInstance";
import ErrorContextProvider from "~/components/ErrorContext/ErrorContext";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import InternalRedirect from "~/components/Navigation/InternalRedirect/InternalRedirect";
import { NoPermissionsPage } from "~/components/NoPermissionsPage/NoPermissionsPage";
import { RedirectAs404 } from "~/components/NotFound/NotFound";
import { hasPermission } from "~/components/PermissionCheck/PermissionCheck";
import RedirectToProcessEditorViaSlug from "~/components/RedirectToProcessEditorViaSlug/RedirectToProcessEditorViaSlug";
import ReloadableRouteContent from "~/components/ReloadableRoute/ReloadableRouteContent";
import { ChannelRedirect, ReleaseRedirect, RunbookRedirect } from "~/components/RootRoutes/ProjectChildRedirect";
import { RunbookSnapshotRedirect } from "~/components/RootRoutes/ProjectRunbookChildRedirect";
import { ProjectTriggerRedirect } from "~/components/RootRoutes/ProjectTriggerRedirect";
import { SpaceRootRedirect } from "~/components/RootRoutes/SpaceRootRedirect";
import { RedirectProjectIfNotSlug, RedirectReleaseVersion } from "~/components/SlugSafeRedirect/SlugSafeRedirect";
import type { SpaceContext } from "~/components/SpaceLoader/SpaceLoader";
import { isSpaceNotFound, isSpecificSpaceContext } from "~/components/SpaceLoader/SpaceLoader";
import { SpaceNotFound } from "~/components/SpaceNotFound/SpaceNotFound";
import { useOctopusFeatureToggle, usePlatformHubOctopusFeatureToggles } from "~/hooks/useOctopusFeatureToggle";
import type { ArgoCDInstancePageContext, ArgoCDInstancePageLoaderContext } from "~/routing/pageRegistrations/ArgoCDInstancePageRegistration";
import type { PageRegistration } from "~/routing/pageRegistrations/PageRegistration";
import type { ProjectPageContext, ProjectPageLoaderContext, ProjectRunbookPageContext, ProjectRunbookPageLoaderContext } from "~/routing/pageRegistrations/ProjectPageRegistration";
import type { StandardLoaderContext } from "~/routing/pageRegistrations/StandardLoaderContext";
import type { StandardPageContext } from "~/routing/pageRegistrations/StandardPageContext";
import { formatRoutePath } from "~/routing/pageRegistrations/formatRoutePath";
import { routeSegment } from "~/routing/pageRegistrations/routeSegment";
import { DefaultConfigurationPageRedirect } from "~/routing/redirects/DefaultConfigurationPageRedirect";
import { DefaultPlatformHubPageRedirect } from "~/routing/redirects/DefaultPlatformHubPageRedirect";
import { PlatformHubLicenseRedirect } from "~/routing/redirects/PlatformHubLicenseRedirect";
import { ScopedUserRoleToTeamRedirect } from "~/routing/redirects/ScopedUserRoleToTeamRedirect";
import { allLegacySpaceRedirectRoutes, allLegacySystemOrMixedScopeRedirectRoutes } from "./allLegacyRedirectRoutes";
import type { SpecificStepTemplatePageContext } from "./pageRegistrations/LibraryPageRegistration";
import { RoutePage } from "./pageRegistrations/RoutePage";
import { allPages } from "./pageRegistrations/allPageRegistrations";
import { DefaultInsightsPageRedirect } from "./redirects/DefaultInsightsPageRedirect";
import { DefaultLibraryPageRedirect } from "./redirects/DefaultLibraryPageRedirect";
interface AllPageRoutesProps {
    spaceContext: SpaceContext;
    loaderContext: StandardLoaderContext;
    pageContext: StandardPageContext;
}
export function AllPageRoutes({ spaceContext, loaderContext, pageContext }: AllPageRoutesProps) {
    // If you are not in a space, then it does not make sense to render any space specific components
    // We are better off letting this fall through to a 404,
    // rather than try and fail to render a space specific component, and probably end up with undefined behaviour
    const showSpaceRoutes = isSpecificSpaceContext(spaceContext);
    // If the space was not found, we don't want to show routes that are available in the system context.
    // We want to direct people to go to either a space-less route, or to step into a space.
    // So we should only show the Space Switcher in the navbar and disallow other routing.
    const showSystemRoutes = !isSpaceNotFound(spaceContext);
    return (<Routes>
            {/*We try to match legacy redirects first, because: */}
            {/*- They might partially match against other route segments and we don't want to interleave them with route segments because this adds more complexity. */}
            {/*- If there's ambiguity between a non-legacy route and a legacy route, we prefer the legacy route. For example:*/}
            {/*    - /projects/new vs /projects/{projectSlug} where {projectSlug} is 'new'. If we preferred the non-legacy route, then we would never match against the legacy route, so why even define it?  */}
            {allLegacySystemOrMixedScopeRedirectRoutes()}

            {getDefaultRootRedirectRoute(spaceContext)}

            {showSpaceRoutes &&
            routeSegment(allPages.childGroups.space, (routeProps, pages, childGroups) => (<Routes>
                        {allLegacySpaceRedirectRoutes()}
                        {getSpaceRoutes(loaderContext, pageContext)}
                        {getSystemOrMixedScopeRoutes(loaderContext, pageContext)}
                        <Route path="*" element={getNoMatchRoute(spaceContext)}/>
                    </Routes>))}
            {showSystemRoutes && getSystemOrMixedScopeRoutes(loaderContext, pageContext)}

            <Route path="*" element={getNoMatchRoute(spaceContext)}/>
        </Routes>);
}
function getDefaultRootRedirectRoute(spaceContext: SpaceContext) {
    if (isSpecificSpaceContext(spaceContext)) {
        // The SpaceLoader redirects to the root of a space, means that this route is never hit. We should consider whether this is still needed.
        return renderRedirect(allRoutes.redirects.rootRedirect, () => <InternalRedirect to={links.projectsPage.generateUrl({ spaceId: spaceContext.Id })}/>);
    }
    if (session.currentPermissions?.hasAnyPermissions()) {
        return renderRedirect(allRoutes.redirects.rootRedirect, () => <InternalRedirect to={links.configurationRootRedirect.generateUrl()}/>);
    }
    return undefined;
}
function getSpaceRoutes(loaderContext: StandardLoaderContext, pageContext: StandardPageContext) {
    return [
        ...renderPagesWithLayout(allPages.childGroups.space.pages, loaderContext, pageContext),
        routeSegment(allPages.childGroups.space.childGroups.projects, (routeProps, pages, childGroups) => <ProjectsRoutes projectsPages={pages} projectsPageGroups={childGroups} loaderContext={loaderContext} pageContext={pageContext}/>),
        routeSegment(allPages.childGroups.space.childGroups.infrastructure, (routeProps, pages, childGroups) => <InfrastructureRoutes pages={pages} childGroups={childGroups} loaderContext={loaderContext} pageContext={pageContext}/>),
        routeSegment(allPages.childGroups.space.childGroups.library, (routeProps, pages, childGroups) => <LibraryRoutes pages={pages} childGroups={childGroups} loaderContext={loaderContext} pageContext={pageContext}/>),
        routeSegment(allPages.childGroups.space.childGroups.tenants, (routeProps, pages, childGroups) => <TenantRoutes tenantsPages={pages} tenantsPageGroups={childGroups} loaderContext={loaderContext} pageContext={pageContext}/>),
        routeSegment(allPages.childGroups.space.childGroups.insights, (routeProps, pages, childGroups) => (<InsightsRoutes pages={pages} childGroups={childGroups} spaceId={routeProps.spaceId} loaderContext={loaderContext} pageContext={pageContext}/>)),
        renderRedirect(allRoutes.childRouteSegments.space.redirects.deploymentToProjectTaskRedirect, ({ spaceId, deploymentId }) => <DeploymentToProjectTaskRedirect spaceId={spaceId} deploymentId={deploymentId}/>),
        renderRedirect(allRoutes.childRouteSegments.space.redirects.spaceRootRedirect, ({ spaceId }) => <SpaceRootRedirect spaceId={spaceId}/>),
        renderRedirect(allRoutes.childRouteSegments.space.redirects.releaseRedirect, ({ spaceId, releaseId }) => <ReleaseRedirect spaceId={spaceId} releaseId={releaseId}/>),
        renderRedirect(allRoutes.childRouteSegments.space.redirects.triggerRedirect, ({ spaceId, triggerId }) => <ProjectTriggerRedirect spaceId={spaceId} triggerId={triggerId}/>),
        renderRedirect(allRoutes.childRouteSegments.space.redirects.channelRedirect, ({ spaceId, channelId }) => <ChannelRedirect spaceId={spaceId} channelId={channelId}/>),
        renderRedirect(allRoutes.childRouteSegments.space.redirects.runbookSnapshotRedirect, ({ spaceId, runbookSnapshotId }) => <RunbookSnapshotRedirect spaceId={spaceId} runbookSnapshotId={runbookSnapshotId}/>),
        renderRedirect(allRoutes.childRouteSegments.space.redirects.runbookRedirect, ({ spaceId, runbookId }) => <RunbookRedirect spaceId={spaceId} runbookId={runbookId}/>),
        renderRedirect(allRoutes.childRouteSegments.space.redirects.runbookRunRedirect, ({ spaceId, runbookRunId }) => <RunbookRunToRunbookTaskRedirect spaceId={spaceId} runbookRunId={runbookRunId}/>),
    ];
}
function getSystemOrMixedScopeRoutes(loaderContext: StandardLoaderContext, pageContext: StandardPageContext) {
    const platformHubSegment = [
        routeSegment(allPages.childGroups.system.childGroups.platformHub, (routeProps, pages, childGroups) => <PlatformHubRoutes pages={pages} childGroups={childGroups} loaderContext={loaderContext} pageContext={pageContext}/>),
    ];
    const systemPages = allPages.childGroups.system.pages;
    return [
        ...renderPagesWithLayout(systemPages, loaderContext, pageContext),
        routeSegment(allPages.childGroups.system.childGroups.configuration, (routeProps, pages, childGroups) => <ConfigurationRoutes pages={pages} childGroups={childGroups} loaderContext={loaderContext} pageContext={pageContext}/>),
        renderRedirect(allRoutes.childRouteSegments.system.redirects.interruptionToTaskRedirect, ({ interruptionId }) => <InterruptionToProjectRedirect interruptionId={interruptionId}/>),
        routeSegment(allPages.childGroups.system.childGroups.myProfile, (routeProps, pages) => <MyProfileRoutes pages={pages} loaderContext={loaderContext} pageContext={pageContext}/>),
        ...platformHubSegment,
    ];
}
function getNoMatchRoute(spaceContext: SpaceContext) {
    if (isSpaceNotFound(spaceContext)) {
        return (<ReloadableRouteContent>
                <SpaceNotFound spaceNotFoundContext={spaceContext}/>
            </ReloadableRouteContent>);
    }
    else if (!session.currentPermissions?.hasAnyPermissions()) {
        return (<ReloadableRouteContent>
                <NoPermissionsPage />
            </ReloadableRouteContent>);
    }
    return <RedirectAs404 />;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RouteParamsFromRouteTemplate<Template extends RouteTemplate<any>> = Template extends RouteTemplate<infer RouteParams> ? RouteParams : never;
const branchNameKey: Extract<keyof RouteParamsFromRouteTemplate<typeof allPages.childGroups.space.childGroups.projects.childGroups.specificProject.childGroups.branch.absoluteTemplate>, "branchName"> = "branchName";
function InfrastructureRoutes({ pages, childGroups, loaderContext, pageContext, }: {
    pages: typeof allPages.childGroups.space.childGroups.infrastructure.pages;
    childGroups: typeof allPages.childGroups.space.childGroups.infrastructure.childGroups;
    loaderContext: StandardLoaderContext;
    pageContext: StandardPageContext;
}) {
    return (<Routes>
            {renderPagesWithLayout(pages, loaderContext, pageContext)}

            {routeSegment(childGroups.deploymentTarget, (routeProps, deploymentTargetPages) => (<MachineLayout machineId={routeProps.machineId}>
                    <Routes>
                        {renderPages(deploymentTargetPages, loaderContext, pageContext)}
                        {renderRedirectAs404Route()}
                    </Routes>
                </MachineLayout>))}

            {routeSegment(childGroups.workerMachine, (routeProps, workerMachinePages) => (<WorkerMachineLayout machineId={routeProps.machineId}>
                    <Routes>
                        {renderPages(workerMachinePages, loaderContext, pageContext)}
                        {renderRedirectAs404Route()}
                    </Routes>
                </WorkerMachineLayout>))}
            {routeSegment(childGroups.argoCDInstance, (routeProps, argoCDPages) => (<ArgoCDInstanceLayout instanceId={routeProps.instanceId}>
                    {(gateway) => {
                const argoCDLoaderContext: ArgoCDInstancePageLoaderContext = {
                    ...loaderContext,
                    gateway: Promise.resolve(gateway),
                };
                const argoCDPageContext: ArgoCDInstancePageContext = {
                    ...pageContext,
                    gateway,
                };
                return (<Routes>
                                {renderPages(argoCDPages, argoCDLoaderContext, argoCDPageContext)}
                                {renderRedirectAs404Route()}
                            </Routes>);
            }}
                </ArgoCDInstanceLayout>))}
            {renderRedirect(allRoutes.childRouteSegments.space.childRouteSegments.infrastructure.redirects.infrastructureRootRedirect, ({ spaceId }) => (<InternalRedirect to={links.infrastructureOverviewPage.generateUrl({ spaceId })}/>))}
            {renderRedirectAs404Route()}
        </Routes>);
}
function LibraryRoutes({ pages, childGroups, loaderContext, pageContext, }: {
    pages: typeof allPages.childGroups.space.childGroups.library.pages;
    childGroups: typeof allPages.childGroups.space.childGroups.library.childGroups;
    loaderContext: StandardLoaderContext;
    pageContext: StandardPageContext;
}) {
    const { gitHubConnectionsPage, addGitHubConnectionPage, gitHubConnectionPage, approvalPoliciesPage, ...otherPages } = pages;
    const gitHubConnectionsEnabled = isFeatureToggleEnabled("GitHubConnectionsFeatureToggle");
    const gitHubConnectionPages = {
        gitHubConnectionsPage,
        addGitHubConnectionPage,
        gitHubConnectionPage,
    };
    const octopusApprovalsFeatureEnabled = isFeatureToggleEnabled("octopus-approvals");
    return (<Routes>
            {renderRedirect(allRoutes.childRouteSegments.space.childRouteSegments.library.redirects.libraryRootRedirect, ({ spaceId }) => (<DefaultLibraryPageRedirect spaceId={spaceId}/>))}

            {renderRedirect(allRoutes.childRouteSegments.space.childRouteSegments.library.redirects.redirectFeedBasedOnType, ({ spaceId, feedId }) => (<RedirectFeedBasedOnType spaceId={spaceId} feedId={feedId}/>))}

            {octopusApprovalsFeatureEnabled && renderPagesWithLayout({ approvalPoliciesPage }, loaderContext, pageContext)}
            {gitHubConnectionsEnabled && renderPagesWithLayout(gitHubConnectionPages, loaderContext, pageContext)}

            {renderPagesWithLayout(otherPages, loaderContext, pageContext)}

            {routeSegment(childGroups.specificStepTemplate, (routeProps, stepTemplatePages) => (<StepTemplateLayout spaceId={routeProps.spaceId} templateId={routeProps.templateId}>
                    {({ onActionTemplateUpdated }) => {
                const specificStepTemplatePageContext: SpecificStepTemplatePageContext = {
                    ...pageContext,
                    onActionTemplateUpdated,
                };
                return (<Routes>
                                {renderPages(stepTemplatePages, loaderContext, specificStepTemplatePageContext)}
                                {renderRedirectAs404Route()}
                            </Routes>);
            }}
                </StepTemplateLayout>))}

            {renderRedirectAs404Route()}
        </Routes>);
}
function ProjectsRoutes({ projectsPages, projectsPageGroups, loaderContext, pageContext, }: {
    projectsPages: typeof allPages.childGroups.space.childGroups.projects.pages;
    projectsPageGroups: typeof allPages.childGroups.space.childGroups.projects.childGroups;
    loaderContext: StandardLoaderContext;
    pageContext: StandardPageContext;
}) {
    const location = useLocation();
    return (<Routes>
            {renderPagesWithLayout(projectsPages, loaderContext, pageContext)}

            {routeSegment(projectsPageGroups.importExports, (routeProps, importExportPages) => (<Routes>
                    {renderPagesWithLayout(importExportPages, loaderContext, pageContext)}
                    {renderRedirectAs404Route()}
                </Routes>))}

            {routeSegment(projectsPageGroups.specificProject, (specificProjectRouteProps, specificProjectPages, specificProjectChildGroups) => (<RedirectProjectIfNotSlug>
                    <Routes>
                        {routeSegment(specificProjectChildGroups.branch, (branchRouteProps, branchPages, branchChildGroups) => (<ProjectLayout spaceId={branchRouteProps.spaceId} projectSlug={branchRouteProps.projectSlug} branchName={branchRouteProps.branchName} isNewlyCreatedProject={new URLSearchParams(location.search).get("newlyCreatedProject")} configureProject={new URLSearchParams(location.search).get("configureProject") === "true"} defaultActionType={new URLSearchParams(location.search).get("actionType")} scrollAreaRef={pageContext.scrollAreaRef} projectStatus={pageContext.projectStatus}>
                                    {({ projectContext, setShowK8sStatusItem }) => {
                    const projectPageContext: ProjectPageContext = {
                        ...pageContext,
                        projectContext,
                        setShowK8sStatusItem,
                    };
                    const projectPageLoaderContext: ProjectPageLoaderContext = {
                        ...loaderContext,
                        projectContext: Promise.resolve(projectContext),
                    };
                    return (<Routes>
                                                {routeSegment(branchChildGroups.deployments, (_, branchDeploymentPages) => (<ErrorContextProvider>
                                                        <Routes>
                                                            {renderRedirect(allRoutes.childRouteSegments.space.childRouteSegments.projects.childRouteSegments.specificProject.childRouteSegments.branch.childRouteSegments.deployments.redirects
                                .branchDeploymentProcessStepByStepSlugRedirect, ({ projectSlug, stepSlug }) => (<RedirectToProcessEditorViaSlug processType={ProcessType.Deployment} projectSlug={projectSlug} stepSlug={stepSlug}/>))}

                                                            {renderPages(branchDeploymentPages, projectPageLoaderContext, projectPageContext)}

                                                            {renderRedirectAs404Route()}
                                                        </Routes>
                                                    </ErrorContextProvider>))}

                                                {routeSegment(branchChildGroups.variables, (_, branchVariablesPages) => (<ErrorContextProvider>
                                                        <Routes>
                                                            {renderPages(branchVariablesPages, projectPageLoaderContext, projectPageContext)}
                                                            {renderRedirectAs404Route()}
                                                        </Routes>
                                                    </ErrorContextProvider>))}

                                                {renderPages(branchPages, projectPageLoaderContext, projectPageContext)}

                                                {renderRedirectAs404Route()}
                                            </Routes>);
                }}
                                </ProjectLayout>), [branchNameKey])}
                        {routeSegment(specificProjectChildGroups.withoutBranch, (branchRouteParams, nonBranchPages, nonBranchChildGroups) => {
                return (<ProjectLayout spaceId={branchRouteParams.spaceId} projectSlug={branchRouteParams.projectSlug} branchName={undefined} isNewlyCreatedProject={new URLSearchParams(location.search).get("newlyCreatedProject")} configureProject={new URLSearchParams(location.search).get("configureProject") === "true"} defaultActionType={new URLSearchParams(location.search).get("actionType")} scrollAreaRef={pageContext.scrollAreaRef} projectStatus={pageContext.projectStatus}>
                                    {({ projectContext, setShowK8sStatusItem }) => {
                        const projectPageContext: ProjectPageContext = {
                            ...pageContext,
                            projectContext,
                            setShowK8sStatusItem,
                        };
                        const projectPageLoaderContext: ProjectPageLoaderContext = {
                            ...loaderContext,
                            projectContext: Promise.resolve(projectContext),
                        };
                        return (<Routes>
                                                {renderRedirect(allRoutes.childRouteSegments.space.childRouteSegments.projects.childRouteSegments.specificProject.childRouteSegments.withoutBranch.redirects.projectRootRedirect, () => (<ProjectToOverviewRedirect />))}
                                                {renderRedirect(allRoutes.childRouteSegments.space.childRouteSegments.projects.childRouteSegments.specificProject.childRouteSegments.withoutBranch.redirects.projectOverviewRedirect, ({ spaceId, projectSlug }) => (<InternalRedirect to={links.projectDashboardPage.generateUrl({ spaceId, projectSlug })}/>))}
                                                {routeSegment(nonBranchChildGroups.deployments, (deploymentRouteParams, deploymentPages, deploymentChildGroups) => (<Routes>
                                                        {routeSegment(deploymentChildGroups.process, (_, deploymentProcessPages) => (<BranchAwareRedirect>
                                                                <ErrorContextProvider>
                                                                    <Routes>
                                                                        {renderRedirect(allRoutes.childRouteSegments.space.childRouteSegments.projects.childRouteSegments.specificProject.childRouteSegments.withoutBranch.childRouteSegments.deployments
                                        .childRouteSegments.process.redirects.deploymentProcessStepByStepSlugRedirect, ({ projectSlug, stepSlug }) => (<RedirectToProcessEditorViaSlug processType={ProcessType.Deployment} projectSlug={projectSlug} stepSlug={stepSlug}/>))}

                                                                        {renderPages(deploymentProcessPages, projectPageLoaderContext, projectPageContext)}

                                                                        {renderRedirectAs404Route()}
                                                                    </Routes>
                                                                </ErrorContextProvider>
                                                            </BranchAwareRedirect>))}
                                                        {routeSegment(deploymentChildGroups.settings, (_, deploymentSettingsPages) => (<BranchAwareRedirect>
                                                                <ErrorContextProvider>
                                                                    <Routes>
                                                                        {renderPages(deploymentSettingsPages, projectPageLoaderContext, projectPageContext)}
                                                                        {renderRedirectAs404Route()}
                                                                    </Routes>
                                                                </ErrorContextProvider>
                                                            </BranchAwareRedirect>))}

                                                        {routeSegment(deploymentChildGroups.specificRelease, (_, releasePages) => (<RedirectReleaseVersion>
                                                                <Routes>
                                                                    {renderPages(releasePages, projectPageLoaderContext, projectPageContext)}
                                                                    {renderRedirectAs404Route()}
                                                                </Routes>
                                                            </RedirectReleaseVersion>))}

                                                        {renderPages(deploymentPages, projectPageLoaderContext, projectPageContext)}
                                                        {renderRedirectAs404Route()}
                                                    </Routes>))}
                                                {routeSegment(nonBranchChildGroups.variables, (_, variablesPages, variablesChildGroups) => (<ErrorContextProvider>
                                                        <Routes>
                                                            {routeSegment(variablesChildGroups.wrapper, (_, variablesWrapperPages) => (<VariablesBranchAwareRedirect>
                                                                    <Routes>
                                                                        {renderPages(variablesWrapperPages, projectPageLoaderContext, projectPageContext)}
                                                                        {renderRedirectAs404Route()}
                                                                    </Routes>
                                                                </VariablesBranchAwareRedirect>))}

                                                            {routeSegment(variablesChildGroups.all, (_, allVariablesPages) => (<VariablesBranchAwareRedirect>
                                                                    <Routes>
                                                                        {renderPages(allVariablesPages, projectPageLoaderContext, projectPageContext)}
                                                                        {renderRedirectAs404Route()}
                                                                    </Routes>
                                                                </VariablesBranchAwareRedirect>))}

                                                            {routeSegment(variablesChildGroups.preview, (_, variablePreviewPages) => (<VariablesBranchAwareRedirect>
                                                                    <Routes>
                                                                        {renderPages(variablePreviewPages, projectPageLoaderContext, projectPageContext)}
                                                                        {renderRedirectAs404Route()}
                                                                    </Routes>
                                                                </VariablesBranchAwareRedirect>))}

                                                            {routeSegment(variablesChildGroups.tenant, (_, projectTenantVariablePages) => (<Routes>
                                                                    {renderRedirect(allRoutes.childRouteSegments.space.childRouteSegments.projects.childRouteSegments.specificProject.childRouteSegments.withoutBranch.childRouteSegments.variables.childRouteSegments
                                        .tenant.redirects.projectTenantsRedirect, () => (<ProjectTenantVariablesRedirect />))}
                                                                    {renderPages(projectTenantVariablePages, projectPageLoaderContext, projectPageContext)}
                                                                    {renderRedirectAs404Route()}
                                                                </Routes>))}

                                                            {renderPages(variablesPages, projectPageLoaderContext, projectPageContext)}
                                                            {renderRedirectAs404Route()}
                                                        </Routes>
                                                    </ErrorContextProvider>))}

                                                {routeSegment(nonBranchChildGroups.specificRunbook, ({ runbookId }, runbookPages, runbookChildGroups) => (<SpecificRunbookRoutes runbookId={runbookId} pages={runbookPages} childGroups={runbookChildGroups} projectLoaderContext={projectPageLoaderContext} projectPageContext={projectPageContext}/>))}

                                                {routeSegment(nonBranchChildGroups.projectRunbooks, (_, projectRunbookPages) => (<ErrorContextProvider>
                                                        <Routes>
                                                            {renderPages(projectRunbookPages, projectPageLoaderContext, projectPageContext)}
                                                            {renderRedirectAs404Route()}
                                                        </Routes>
                                                    </ErrorContextProvider>))}

                                                {renderPages(nonBranchPages, projectPageLoaderContext, projectPageContext)}
                                                {renderRedirectAs404Route()}
                                            </Routes>);
                    }}
                                </ProjectLayout>);
            })}
                        {renderRedirectAs404Route()}
                    </Routes>
                </RedirectProjectIfNotSlug>))}
            {renderRedirectAs404Route()}
        </Routes>);
}
type SpecificRunbookPages = typeof allPages.childGroups.space.childGroups.projects.childGroups.specificProject.childGroups.withoutBranch.childGroups.specificRunbook.pages;
type SpecificRunbookChildGroups = typeof allPages.childGroups.space.childGroups.projects.childGroups.specificProject.childGroups.withoutBranch.childGroups.specificRunbook.childGroups;
function SpecificRunbookRoutes({ runbookId, pages, childGroups, projectLoaderContext, projectPageContext, }: {
    runbookId: string;
    pages: SpecificRunbookPages;
    childGroups: SpecificRunbookChildGroups;
    projectLoaderContext: ProjectPageLoaderContext;
    projectPageContext: ProjectPageContext;
}) {
    const newRunbookRunsListPageEnabled = useOctopusFeatureToggle("new-runbook-runs-list-page", false);
    return (<RunbookContextLayout runbookId={runbookId}>
            {(runbookContextWithLoadedRunbook) => {
            const projectRunbookPageContext: ProjectRunbookPageContext = { ...projectPageContext, runbookContext: runbookContextWithLoadedRunbook };
            const projectRunbookPageLoaderContext: ProjectRunbookPageLoaderContext = { ...projectLoaderContext, runbookContext: Promise.resolve(runbookContextWithLoadedRunbook) };
            return (<Routes>
                        {renderRedirect(allRoutes.childRouteSegments.space.childRouteSegments.projects.childRouteSegments.specificProject.childRouteSegments.withoutBranch.childRouteSegments.specificRunbook.redirects.runbookRootRedirect, ({ spaceId, projectSlug, runbookId }) => {
                    // We want to redirect to the new runbook runs list page if the feature toggle is enabled or
                    // if the runbook is a Git runbook.
                    if (newRunbookRunsListPageEnabled) {
                        return <InternalRedirect to={links.runbookRunsPage.generateUrl({ spaceId, projectSlug, runbookId })}/>;
                    }
                    return <InternalRedirect to={links.runbookOverviewPage.generateUrl({ spaceId, projectSlug, runbookId })}/>;
                })}

                        {routeSegment(childGroups.process, (_, runbookProcessPages) => (<ErrorContextProvider>
                                <Routes>
                                    {renderRedirect(allRoutes.childRouteSegments.space.childRouteSegments.projects.childRouteSegments.specificProject.childRouteSegments.withoutBranch.childRouteSegments.specificRunbook.childRouteSegments.process.redirects
                        .projectRunbookProcessStepByStepSlugRedirect, ({ projectSlug, processId, stepSlug }) => (<RedirectToProcessEditorViaSlug processType={ProcessType.Runbook} projectSlug={projectSlug} processId={processId} stepSlug={stepSlug}/>))}
                                    {renderPages(runbookProcessPages, projectRunbookPageLoaderContext, projectRunbookPageContext)}
                                </Routes>
                            </ErrorContextProvider>))}

                        {renderPages(pages, projectRunbookPageLoaderContext, projectRunbookPageContext)}

                        {renderRedirectAs404Route()}
                    </Routes>);
        }}
        </RunbookContextLayout>);
}
function PlatformHubRoutes({ pages, childGroups, loaderContext, pageContext, }: {
    pages: typeof allPages.childGroups.system.childGroups.platformHub.pages;
    childGroups: typeof allPages.childGroups.system.childGroups.platformHub.childGroups;
    loaderContext: StandardLoaderContext;
    pageContext: StandardPageContext;
}) {
    const { platformHubUpsellPage, platformHubVersionControlSettingsPage, policiesPage, ...processTemplates } = pages;
    const platformHubFeature = usePlatformHubOctopusFeatureToggles();
    if (!hasPermission(Permission.PlatformHubView)) {
        return <NoPermissionsPage />;
    }
    return (<PlatformHubLicenseRedirect>
            <Routes>
                {renderRedirect(allRoutes.childRouteSegments.system.childRouteSegments.platformHub.redirects.platformHubRootRedirect, () => (<DefaultPlatformHubPageRedirect />))}
                {renderPageWithLayout(platformHubUpsellPage, loaderContext, pageContext)}
                {renderPageWithLayout(platformHubVersionControlSettingsPage, loaderContext, pageContext)}
                {renderPagesWithLayout(processTemplates, loaderContext, pageContext)}
                {platformHubFeature.compliancePolicies ? renderPageWithLayout(policiesPage, loaderContext, pageContext) : null}

                {platformHubFeature.projectTemplates
            ? routeSegment(childGroups.projectTemplates, (routeProps, projectTemplatePages, childGroups) => (<Routes>
                              {renderPagesWithLayout(projectTemplatePages, loaderContext, pageContext)}
                              {routeSegment(childGroups.specificProject, (routeProps, pages, childGroups) => (<ProjectTemplateSpecificRoutes pages={pages} childGroups={childGroups} loaderContext={loaderContext} pageContext={pageContext}/>))}
                              {renderRedirectAs404Route()}
                          </Routes>))
            : null}
                {renderRedirectAs404Route()}
            </Routes>
        </PlatformHubLicenseRedirect>);
}
function ProjectTemplateSpecificRoutes({ childGroups, loaderContext, pageContext, }: {
    pages: typeof allPages.childGroups.system.childGroups.platformHub.childGroups.projectTemplates.childGroups.specificProject.pages;
    childGroups: typeof allPages.childGroups.system.childGroups.platformHub.childGroups.projectTemplates.childGroups.specificProject.childGroups;
    loaderContext: StandardLoaderContext;
    pageContext: StandardPageContext;
}) {
    return (<Routes>
            {routeSegment(childGroups.branch, (branchRouteProps, branchPages, branchChildGroups) => (<ProjectTemplateLayout slug={branchRouteProps.slug} branchName={branchRouteProps.gitRef} scrollAreaRef={pageContext.scrollAreaRef} projectStatus={pageContext.projectStatus}>
                        {({ projectTemplateContext, projectStatus, projectTemplateStatus }) => {
                return (<Routes>
                                    {routeSegment(branchChildGroups.variables, (_, branchVariablesPages) => (<ErrorContextProvider>
                                            <Routes>
                                                {renderPages(branchVariablesPages, loaderContext, pageContext)}
                                                {renderRedirectAs404Route()}
                                            </Routes>
                                        </ErrorContextProvider>))}
                                    {routeSegment(branchChildGroups.parameters, (_, branchParametersPages) => (<ErrorContextProvider>
                                            <Routes>
                                                {renderPages(branchParametersPages, loaderContext, pageContext)}
                                                {renderRedirectAs404Route()}
                                            </Routes>
                                        </ErrorContextProvider>))}
                                    {renderPages(branchPages, loaderContext, pageContext)}
                                    {renderRedirectAs404Route()}
                                </Routes>);
            }}
                    </ProjectTemplateLayout>), ["gitRef"])}
            {renderRedirectAs404Route()}
        </Routes>);
}
function ConfigurationRoutes({ pages, childGroups, loaderContext, pageContext, }: {
    pages: typeof allPages.childGroups.system.childGroups.configuration.pages;
    childGroups: typeof allPages.childGroups.system.childGroups.configuration.childGroups;
    loaderContext: StandardLoaderContext;
    pageContext: StandardPageContext;
}) {
    const deprecationsConfigurationPageEnabled = isFeatureToggleEnabled("DeprecationsConfigurationFeatureToggle");
    const { deprecationsPage, ...otherPages } = pages;
    return (<ConfigurationLayout>
            <Routes>
                {renderRedirect(allRoutes.childRouteSegments.system.childRouteSegments.configuration.redirects.configurationRootRedirect, () => (<DefaultConfigurationPageRedirect />))}
                {renderRedirect(allRoutes.childRouteSegments.system.childRouteSegments.configuration.redirects.scopedUserRoleToTeamRedirect, ({ scopedRoleId }) => (<ScopedUserRoleToTeamRedirect scopedUserRoleId={scopedRoleId}/>))}
                {renderRedirect(allRoutes.childRouteSegments.system.childRouteSegments.configuration.redirects.diagnosticMachineCleanupRedirect, () => (<InternalRedirect to={links.auditPage.generateUrl({ documentTypes: ["Machines"], users: ["users-system"], eventCategories: ["Deleted"] })}/>))}
                {renderPages(otherPages, loaderContext, pageContext)}
                {deprecationsConfigurationPageEnabled && renderPage(deprecationsPage, loaderContext, pageContext)}
                {routeSegment(childGroups.settings, (routeProps, settingsPages) => (<Routes>
                        {renderPages(settingsPages, loaderContext, pageContext)}
                        {renderRedirectAs404Route()}
                    </Routes>))}
                {renderRedirectAs404Route()}
            </Routes>
        </ConfigurationLayout>);
}
function MyProfileRoutes({ pages, loaderContext, pageContext }: {
    pages: typeof allPages.childGroups.system.childGroups.myProfile.pages;
    loaderContext: StandardLoaderContext;
    pageContext: StandardPageContext;
}) {
    return (<MyProfileLayout>
            <Routes>
                {renderRedirect(allRoutes.childRouteSegments.system.childRouteSegments.myProfile.redirects.myProfileRootRedirect, () => (<InternalRedirect to={links.myProfilePage.generateUrl()}/>))}
                {renderPages(pages, loaderContext, pageContext)}

                {renderRedirectAs404Route()}
            </Routes>
        </MyProfileLayout>);
}
function InsightsRoutes({ pages, childGroups, loaderContext, pageContext, }: {
    pages: typeof allPages.childGroups.space.childGroups.insights.pages;
    childGroups: typeof allPages.childGroups.space.childGroups.insights.childGroups;
    spaceId: string;
    loaderContext: StandardLoaderContext;
    pageContext: StandardPageContext;
}) {
    return (<Routes>
            {renderRedirect(allRoutes.childRouteSegments.space.childRouteSegments.insights.redirects.insightsRootRedirect, ({ spaceId }) => (<DefaultInsightsPageRedirect spaceId={spaceId}/>))}

            {renderPagesWithLayout(pages, loaderContext, pageContext)}

            {routeSegment(childGroups.reports, ({ spaceId }, reportsPages, reportsPagesChildren) => (<LicenseChecker spaceId={spaceId}>
                    <Routes>
                        {renderPagesWithLayout(reportsPages, loaderContext, pageContext)}
                        {routeSegment(reportsPagesChildren.specificReport, ({ spaceId, reportId }, reportPages) => (<InsightsReportLoader reportId={reportId} pageContext={pageContext}>
                                {(insightsReportPageContext) => (<Routes>
                                        {renderPages(reportPages, loaderContext, insightsReportPageContext)}
                                        {renderRedirectAs404Route()}
                                    </Routes>)}
                            </InsightsReportLoader>))}
                    </Routes>
                </LicenseChecker>))}
        </Routes>);
}
function TenantRoutes({ tenantsPages, tenantsPageGroups, loaderContext, pageContext, }: {
    tenantsPages: typeof allPages.childGroups.space.childGroups.tenants.pages;
    tenantsPageGroups: typeof allPages.childGroups.space.childGroups.tenants.childGroups;
    loaderContext: StandardLoaderContext;
    pageContext: StandardPageContext;
}) {
    return (<Routes>
            {routeSegment(tenantsPageGroups.specificTenant, ({ spaceId, tenantId }, specificTenantPages) => (<TenantLayout spaceId={spaceId} tenantId={tenantId}>
                    <Routes>
                        {renderPages(specificTenantPages, loaderContext, pageContext)}
                        {renderRedirectAs404Route()}
                    </Routes>
                </TenantLayout>))}
            {renderPagesWithLayout(tenantsPages, loaderContext, pageContext)}
            {renderRedirectAs404Route()}
        </Routes>);
}
/**
 * Renders a single page, with no layout.
 * Use this when the layout is manually rendered within AllPageRoutes. Otherwise use {@link renderPageWithLayout}.
 * @param pageRegistration The page registration to render.
 * @param loaderContext The LoaderContext to provide to the page
 * @param pageContext The PageContext to provide to the page.
 */
function renderPage<RouteParams, QueryParams extends UnknownQueryParam[], LoaderData, LoaderContext, PageContext>(pageRegistration: PageRegistration<RouteParams, QueryParams, LoaderData, LoaderContext, PageContext>, loaderContext: LoaderContext, pageContext: PageContext) {
    const routePath = formatRoutePath(pageRegistration.route.relativeTemplate);
    return (<Route key={pageRegistration.pageId} path={routePath} element={<RoutePage<RouteParams, QueryParams, LoaderData, LoaderContext, PageContext> 
        // This `key` prop is necessary to ensure the RoutePage is re-mounted when the route changes. The `<Route>` component is not rendered in React Router v6 (https://reactrouter.com/6.30.1/start/faq#route-doesnt-render-how-do-i-compose), so we cannot use its key to ensure re-mounting anymore.
        key={pageRegistration.pageId} loaderContext={loaderContext} pageRegistration={pageRegistration} pageContext={pageContext}/>}/>);
}
/**
 * Renders a single page, along with the layout inferred from the page registration's level.
 * If the layout is rendered within AllPageRoutes, use {@link renderPage} instead
 * @param pageRegistration The page registration to render.
 * @param loaderContext The LoaderContext to provide to the page
 * @param pageContext The PageContext to provide to the page.
 */
function renderPageWithLayout<RouteParams, QueryParams extends UnknownQueryParam[], LoaderData, LoaderContext, PageContext>(pageRegistration: PageRegistration<RouteParams, QueryParams, LoaderData, LoaderContext, PageContext>, loaderContext: LoaderContext, pageContext: PageContext) {
    const routePath = formatRoutePath(pageRegistration.route.relativeTemplate);
    return (<Route key={pageRegistration.pageId} path={routePath} element={<RoutePage<RouteParams, QueryParams, LoaderData, LoaderContext, PageContext> 
        // This `key` prop is necessary to ensure the RoutePage is re-mounted when the route changes. The `<Route>` component is not rendered in React Router v6 (https://reactrouter.com/6.30.1/start/faq#route-doesnt-render-how-do-i-compose), so we cannot use its key to ensure re-mounting anymore.
        key={pageRegistration.pageId} loaderContext={loaderContext} pageRegistration={pageRegistration} pageContext={pageContext} renderPageLayout={(page) => (pageRegistration.level === 1 ? <>{page}</> : <Level2OuterPageLayout>{page}</Level2OuterPageLayout>)}/>}/>);
}
function renderPages<LoaderContext, PageContext, PageRegistrations extends Record<string, UnknownPageRegistration<LoaderContext, PageContext>>>(pageRegistrations: PageRegistrations, loaderContext: LoaderContext, pageContext: PageContext) {
    return Object.values(pageRegistrations).map((p) => renderPage(p, loaderContext, pageContext));
}
function renderPagesWithLayout<LoaderContext, PageContext, PageRegistrations extends Record<string, UnknownPageRegistration<LoaderContext, PageContext>>>(pageRegistrations: PageRegistrations, loaderContext: LoaderContext, pageContext: PageContext) {
    return Object.values(pageRegistrations).map((p) => renderPageWithLayout(p, loaderContext, pageContext));
}
function renderRedirect<TRouteParams>(path: RedirectRouteDefinition<TRouteParams>, render: (params: TRouteParams) => React.ReactElement) {
    const formattedPath = formatRoutePath(path.routeDefinition.relativeTemplate);
    return (<Route key={formattedPath} path={formattedPath} element={<ReloadableRouteContent>
                    <RedirectRouteContent<TRouteParams> render={render}/>
                </ReloadableRouteContent>}/>);
}
function RedirectRouteContent<TRouteParams>({ render }: {
    render: (params: TRouteParams) => React.ReactElement;
}) {
    const params = useParams();
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return render(params as TRouteParams);
}
function renderRedirectAs404Route() {
    return <Route path={"*"} element={<RedirectAs404 />}/>;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UnknownPageRegistration<LoaderContext, PageContext> = PageRegistration<any, UnknownQueryParam[], any, LoaderContext, PageContext>;
